<template>
  <div class="deals-page">
    <div class="container container--fluid">
      <header class="deals-page__header">
        <div class="deals-page__title-row">
          <div class="deals-page__title-group">
            <h1 class="deals-page__title">{{ $t('deals.title') }}</h1>
            <div class="deals-page__pipeline" v-click-outside="closePipelineMenu">
              <button
                type="button"
                class="deals-page__pipeline-trigger"
                :aria-expanded="pipelineMenuOpen"
                @click="pipelineMenuOpen = !pipelineMenuOpen"
              >
                <span>{{ currentPipelineName }}</span>
                <img src="/icons/pipeline-chevron.svg" width="16" height="16" alt="" />
              </button>
              <div v-if="pipelineMenuOpen" class="deals-page__menu">
                <button
                  v-for="pipeline in pipelines"
                  :key="pipeline.id"
                  type="button"
                  class="deals-page__menu-item"
                  :class="{ 'deals-page__menu-item--active': pipeline.id === currentPipelineId }"
                  @click="selectPipeline(pipeline.id)"
                >
                  {{ pipeline.name }}
                </button>
                <p v-if="pipelines.length === 0" class="deals-page__menu-empty">
                  {{ $t('deals.no_pipelines_title') }}
                </p>
              </div>
            </div>
          </div>

          <div class="deals-page__actions">
            <button
              type="button"
              class="deals-page__icon-btn"
              :disabled="exporting || pipelines.length === 0"
              :aria-label="$t('deals.export')"
              @click="handleExport"
            >
              <img src="/icons/pipeline-download.svg" width="16" height="16" alt="" />
            </button>
            <button type="button" class="deals-page__mail-btn" @click="openMailInbox">
              <img src="/icons/pipeline-mail.svg" width="16" height="16" alt="" />
              <span>{{ $t('deals.mail_inbox') }}</span>
              <span v-if="inboxCount > 0" class="deals-page__mail-badge">{{ inboxCount }}</span>
            </button>
            <button type="button" class="deals-page__add-btn" @click="openCreateDealModal()">
              <span>{{ $t('deals.add_deal') }}</span>
              <img src="/icons/pipeline-plus.svg" width="16" height="16" alt="" />
            </button>
          </div>
        </div>

        <div class="deals-page__toolbar">
          <div class="deals-page__toolbar-left">
            <div class="deals-page__view-toggle" role="tablist">
              <button
                type="button"
                role="tab"
                class="deals-page__view-option"
                :class="{ 'deals-page__view-option--active': viewMode === 'kanban' }"
                :aria-selected="viewMode === 'kanban'"
                @click="viewMode = 'kanban'"
              >
                <img src="/icons/pipeline-board.svg" width="16" height="16" alt="" />
                <span>{{ $t('deals.kanban_view') }}</span>
              </button>
              <button
                type="button"
                role="tab"
                class="deals-page__view-option"
                :class="{ 'deals-page__view-option--active': viewMode === 'list' }"
                :aria-selected="viewMode === 'list'"
                @click="viewMode = 'list'"
              >
                <img src="/icons/pipeline-list.svg" width="16" height="16" alt="" />
                <span>{{ $t('deals.list_view') }}</span>
              </button>
              <button
                type="button"
                role="tab"
                class="deals-page__view-option"
                :class="{ 'deals-page__view-option--active': viewMode === 'journey' }"
                :aria-selected="viewMode === 'journey'"
                @click="viewMode = 'journey'"
              >
                <img src="/icons/pipeline-journey.svg" width="16" height="16" alt="" />
                <span>{{ $t('journey.board.view') }}</span>
              </button>
            </div>

            <div class="deals-page__stats" v-click-outside="closeStatsMenu">
              <button
                type="button"
                class="deals-page__stats-trigger"
                :aria-expanded="statsMenuOpen"
                @click="statsMenuOpen = !statsMenuOpen"
              >
                <img src="/icons/pipeline-stats.svg" width="16" height="16" alt="" />
                <span>{{ $t('deals.statistics') }}</span>
                <img src="/icons/pipeline-chevron.svg" width="16" height="16" alt="" />
              </button>
              <div v-if="statsMenuOpen" class="deals-page__menu deals-page__menu--stats">
                <div class="deals-page__stat-row">
                  <span>{{ $t('deals.total_deals') }}</span>
                  <strong>{{ kanbanTotals?.total_deals || 0 }}</strong>
                </div>
                <div class="deals-page__stat-row">
                  <span>{{ $t('deals.total_value') }}</span>
                  <strong>{{ formatCurrency(kanbanTotals?.total_value) }}</strong>
                </div>
                <div class="deals-page__stat-row">
                  <span>{{ $t('deals.weighted_value') }}</span>
                  <strong>{{ formatCurrency(kanbanTotals?.weighted_value) }}</strong>
                </div>
                <button type="button" class="deals-page__menu-link" @click="goToStats">
                  {{ $t('deals.open_statistics') }}
                </button>
              </div>
            </div>
          </div>

          <div class="deals-page__filters">
            <label class="deals-page__search">
              <img src="/icons/pipeline-search.svg" width="16" height="16" alt="" />
              <input
                id="deals-search"
                type="text"
                :value="searchQuery"
                :placeholder="$t('common.search')"
                @input="handleSearch($event.target.value)"
              />
            </label>

            <div class="deals-page__owner" v-click-outside="closeOwnerMenu">
              <button
                type="button"
                class="deals-page__owner-trigger"
                :class="{ 'deals-page__owner-trigger--active': !!selectedOwner }"
                :aria-expanded="ownerMenuOpen"
                @click="ownerMenuOpen = !ownerMenuOpen"
              >
                <span v-if="ownerAvatars.length" class="deals-page__owner-avatars">
                  <span
                    v-for="avatar in ownerAvatars"
                    :key="avatar.id"
                    class="deals-page__owner-avatar"
                    :style="{ backgroundColor: avatar.background, color: avatar.color }"
                  >
                    {{ avatar.initials }}
                  </span>
                </span>
                <span>{{ selectedOwnerLabel }}</span>
                <img src="/icons/pipeline-chevron.svg" width="16" height="16" alt="" />
              </button>
              <div v-if="ownerMenuOpen" class="deals-page__menu deals-page__menu--right">
                <button
                  v-for="option in ownerOptions"
                  :key="option.value || 'all'"
                  type="button"
                  class="deals-page__menu-item"
                  :class="{ 'deals-page__menu-item--active': option.value === selectedOwner }"
                  @click="selectOwner(option.value)"
                >
                  <span
                    v-if="option.avatar"
                    class="deals-page__menu-avatar"
                    :style="{
                      backgroundColor: option.avatar.background,
                      color: option.avatar.color,
                    }"
                  >
                    {{ option.avatar.initials }}
                  </span>
                  <span v-else class="deals-page__menu-avatar deals-page__menu-avatar--all">
                    <BaseIcon name="users" size="12" />
                  </span>
                  <span>{{ option.label }}</span>
                </button>
              </div>
            </div>

            <DropdownMenu class="deals-filter">
              <template #trigger>
                <button
                  type="button"
                  class="deals-page__tags-trigger"
                  :class="{ 'deals-page__tags-trigger--active': hasActiveTagFilter }"
                >
                  <img src="/icons/pipeline-tags.svg" width="16" height="16" alt="" />
                  <span>{{ $t('deals.filter_tags') }}</span>
                  <span v-if="hasActiveTagFilter" class="deals-page__tags-count">
                    {{ selectedTagIds.length }}
                  </span>
                  <img src="/icons/pipeline-chevron.svg" width="16" height="16" alt="" />
                </button>
              </template>
              <template #items>
                <div class="deals-filter__header">
                  <span>{{ $t('deals.filter_by_tags') }}</span>
                  <button
                    v-if="hasActiveTagFilter"
                    type="button"
                    class="deals-filter__reset"
                    @click="clearTagFilter"
                  >
                    {{ $t('deals.filter_reset') }}
                  </button>
                </div>
                <div class="deals-filter__options">
                  <label
                    v-for="tag in tagOptions"
                    :key="tag.value"
                    class="deals-filter__option"
                  >
                    <input
                      type="checkbox"
                      :value="tag.value"
                      v-model="selectedTagIds"
                      @change="applyTagFilter"
                    />
                    <span class="deals-filter__tag" :style="tagChipStyle(tag.color)">
                      {{ tag.label }}
                    </span>
                  </label>
                  <div v-if="tagOptions.length === 0" class="deals-filter__empty">
                    {{ $t('deals.filter_no_tags') }}
                  </div>
                </div>
              </template>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <Teleport to="body">
        <Transition name="deals-bulk">
          <div v-if="hasSelection" class="deals-page__bulk-dock">
            <div
              class="deals-page__bulk-actions"
              role="toolbar"
              :aria-label="$t('deals.selected_count', { count: selectionCount })"
            >
              <div class="deals-page__bulk-count">
                <span>{{ $t('deals.selected_count', { count: selectionCount }) }}</span>
                <button
                  type="button"
                  class="deals-page__bulk-clear"
                  :aria-label="$t('common.clear_selection')"
                  @click="clearSelection"
                >
                  <BaseIcon name="xmark" size="12" />
                </button>
              </div>
              <span class="deals-page__bulk-divider"></span>
              <div class="deals-page__bulk-move" v-click-outside="closeBulkMoveMenu">
                <button
                  type="button"
                  class="deals-page__bulk-btn"
                  :aria-expanded="bulkMoveOpen"
                  aria-haspopup="menu"
                  @click="bulkMoveOpen = !bulkMoveOpen"
                >
                  <BaseIcon name="arrow-right" size="14" />
                  {{ $t('deals.bulk_move') }}
                  <BaseIcon name="chevron" size="12" />
                </button>
                <div v-if="bulkMoveOpen" class="deals-page__bulk-menu" role="menu">
                  <p class="deals-page__bulk-menu-label">{{ $t('deals.bulk_move_to') }}</p>
                  <button
                    v-for="stage in kanbanStages"
                    :key="stage.id"
                    type="button"
                    class="deals-page__bulk-menu-item"
                    role="menuitem"
                    @click="handleBulkMove(stage.id)"
                  >
                    <span
                      class="deals-page__bulk-menu-dot"
                      :style="{ backgroundColor: stage.color || '#434f6f' }"
                    ></span>
                    <span>{{ stage.name }}</span>
                  </button>
                </div>
              </div>
              <button type="button" class="deals-page__bulk-btn" @click="handleBulkArchive">
                <BaseIcon name="package" size="14" />
                {{ $t('deals.bulk_archive') }}
              </button>
            </div>
          </div>
        </Transition>
      </Teleport>

      <!-- Loading State: kanban-shaped skeleton -->
      <div
        class="deals-page__skeleton"
        v-if="pipelinesLoading || kanbanLoading"
        aria-busy="true"
        :aria-label="$t('common.loading')"
      >
        <div class="deals-page__skeleton-column" v-for="col in 4" :key="col">
          <div class="deals-page__skeleton-head">
            <Skeleton variant="text" width="44%" height="0.875rem" />
            <Skeleton variant="text" width="22px" height="0.875rem" />
          </div>
          <div class="deals-page__skeleton-card" v-for="card in 3" :key="card">
            <Skeleton variant="text" width="68%" height="0.875rem" />
            <Skeleton variant="text" width="40%" height="1.125rem" />
            <Skeleton variant="text" width="56%" height="0.75rem" />
          </div>
        </div>
      </div>

      <!-- Empty State: No Pipelines -->
      <EmptyState
        v-else-if="pipelines.length === 0"
        icon="layers"
        :title="$t('deals.no_pipelines_title')"
        :description="$t('deals.no_pipelines_description')"
      >
        <Btn variant="primary" @click="goToPipelineSettings">
          {{ $t('deals.create_pipeline') }}
        </Btn>
      </EmptyState>

      <!-- Empty Filtered State -->
      <EmptyState
        v-else-if="hasActiveFilters && filteredDealCount === 0"
        icon="filter"
        :title="$t('deals.no_deals_filtered_title')"
        :description="$t('deals.no_deals_filtered_description')"
      >
        <Btn variant="secondary" @click="clearAllFilters">
          {{ $t('deals.filter_clear_all') }}
        </Btn>
      </EmptyState>

      <DealJourneyBoard
        v-else-if="viewMode === 'journey'"
        :members="members"
        @card-click="handleDealClick"
        @assign-owner="handleAssignOwner"
      />

      <!-- List View -->
      <DealListView
        v-else-if="viewMode === 'list'"
        :stages="kanbanStages"
        :archive="kanbanArchive"
        :archive-expanded="archiveExpanded"
        :selected-deal-ids="selectedDealIds"
        @card-click="handleDealClick"
        @card-select="handleDealSelect"
        @update:archive-expanded="archiveExpanded = $event"
      />

      <!-- Kanban Board -->
      <DealKanbanBoard
        v-else
        :stages="kanbanStages"
        :archive="kanbanArchive"
        :archive-expanded="archiveExpanded"
        :members="members"
        :selected-deal-ids="selectedDealIds"
        @card-click="handleDealClick"
        @card-select="handleDealSelect"
        @card-email="handleDealEmail"
        @assign-owner="handleAssignOwner"
        @stage-change="handleStageChange"
        @add-deal="openCreateDealModal"
        @archive-deal="handleArchiveDrop"
        @update:archive-expanded="archiveExpanded = $event"
      />

      <!-- Deal Detail Drawer -->
      <DealDetailDrawer
        :show="showDealDrawer"
        :deal-id="selectedDealId"
        show-navigation
        @close="closeDealDrawer"
        @navigate-deal="handleDealClick"
        @deal-updated="handleDealUpdated"
        @deal-deleted="handleDealDeleted"
      />
    </div>
  </div>
</template>

<script>
  import { ref, computed, onMounted, watch } from 'vue';
  import { useRouter, useRoute } from 'vue-router';
  import { useDealStore } from '@/store/dealStore';
  import { useUIStore } from '@/store/uiStore';
  import { useToastStore } from '@/store/toastStore';
  import { useTagStore } from '@/store/tagStore';
  import { useUserStore } from '@/store/userStore';
  import { useI18n } from '@/composables/useI18n';
  import { dealService } from '@/services/dealService';
  import { dealEmailService } from '@/services/dealEmailService';
  import { useCustomerEmail } from '@/composables/useCustomerEmail';
  import { crmService } from '@/services/crmService';
  import OrganizationService from '@/services/organizationService';

  import Btn from '@/components/partials/Btn.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import EmptyState from '@/components/partials/EmptyState.vue';
  import Skeleton from '@shared/components/Skeleton.vue';
  import DropdownMenu from '@/components/partials/DropdownMenu.vue';
  import DealKanbanBoard from '@/components/deals/DealKanbanBoard.vue';
  import DealListView from '@/components/deals/DealListView.vue';
  import DealJourneyBoard from '@/components/deals/DealJourneyBoard.vue';
  import DealDetailDrawer from '@/components/deals/DealDetailDrawer.vue';
  import CreateDealModal from '@/components/deals/CreateDealModal.vue';
  import CustomerMailInboxModal from '@/components/deals/CustomerMailInboxModal.vue';
  export default {
    name: 'DealsPage',

    components: {
      Btn,
      BaseIcon,
      EmptyState,
      Skeleton,
      DropdownMenu,
      DealKanbanBoard,
      DealListView,
      DealJourneyBoard,
      DealDetailDrawer,
    },

    setup() {
      const router = useRouter();
      const route = useRoute();
      const dealStore = useDealStore();
      const uiStore = useUIStore();
      const toastStore = useToastStore();
      const tagStore = useTagStore();
      const userStore = useUserStore();
      const { t } = useI18n();
      const organizationService = new OrganizationService();

      // Local state
      const searchQuery = ref('');
      const searchTimeout = ref(null);
      const bulkMoveOpen = ref(false);
      const archiveExpanded = ref(false);
      const isMobile = window.innerWidth <= 992;
      const viewMode = ref(isMobile ? 'list' : 'kanban');
      const pipelineMenuOpen = ref(false);
      const ownerMenuOpen = ref(false);
      const statsMenuOpen = ref(false);
      const ownerAvatarPalette = [
        { background: '#dbeafe', color: '#1d4ed8' },
        { background: '#ffedd5', color: '#c2410c' },
        { background: '#dcfce7', color: '#15803d' },
      ];

      // Filter state (server-side; mirrors dealStore.filters)
      const members = ref([]);
      const selectedOwner = ref('');
      const selectedTagIds = ref([]);

      // Store state
      const pipelines = computed(() => dealStore.pipelines);
      const pipelinesLoading = computed(() => dealStore.pipelinesLoading);
      const currentPipelineId = computed(() => dealStore.currentPipelineId);
      const kanbanStages = computed(() => dealStore.kanbanStages);
      const kanbanArchive = computed(() => dealStore.kanbanArchive);
      const kanbanTotals = computed(() => dealStore.kanbanTotals);
      const kanbanLoading = computed(() => dealStore.kanbanLoading);
      const exporting = computed(() => dealStore.exporting);
      const inboxCount = ref(0);
      const { crmEmailEnabled: dealEmailEnabled, composeEmail } = useCustomerEmail();
      const showDealDrawer = computed(() => dealStore.showDealDrawer);
      const selectedDealId = computed(() => dealStore.selectedDealId);
      const selectedDealIds = computed(() => dealStore.selectedDealIds);
      const hasSelection = computed(() => dealStore.hasSelection);
      const selectionCount = computed(() => dealStore.selectionCount);

      // Pipeline options for select
      const currentPipelineName = computed(() => {
        const pipeline = pipelines.value.find((item) => item.id === currentPipelineId.value);
        return pipeline?.name || t('deals.select_pipeline');
      });

      const memberAvatar = (member, index) => {
        const user = member.user || member;
        const first = user?.first_name || '';
        const last = user?.last_name || '';
        const palette = ownerAvatarPalette[index % ownerAvatarPalette.length];
        return {
          initials:
            (first.charAt(0) + last.charAt(0)).toUpperCase() ||
            (user?.email || member.email || '?').charAt(0).toUpperCase(),
          background: palette.background,
          color: palette.color,
        };
      };

      const ownerAvatars = computed(() => {
        return members.value.slice(0, 3).map((member, index) => {
          const user = member.user || member;
          return {
            id: user?.id ?? member.user_id ?? index,
            ...memberAvatar(member, index),
          };
        });
      });

      const selectedOwnerLabel = computed(() => {
        if (!selectedOwner.value) return t('deals.filter_owner');
        const match = ownerOptions.value.find((option) => option.value === selectedOwner.value);
        return match?.label || t('deals.filter_owner');
      });

      // Owner filter options (organization members), prefixed with "all owners"
      const ownerOptions = computed(() => {
        const options = members.value.map((member, index) => {
          const user = member.user || member;
          const id = user?.id ?? member.user_id;
          const label =
            [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() ||
            user?.email ||
            member.email ||
            id;
          return { value: id, label, avatar: memberAvatar(member, index) };
        });
        return [{ value: '', label: t('deals.filter_owner_all'), avatar: null }, ...options];
      });

      // Tag filter options ({ value, label, color })
      const tagOptions = computed(() => tagStore.tagOptions);

      // Active filter helpers
      const hasActiveTagFilter = computed(() => selectedTagIds.value.length > 0);
      const hasActiveFilters = computed(
        () => !!selectedOwner.value || hasActiveTagFilter.value
      );

      // The kanban data is already filtered server-side, so we render it directly.
      const filteredDealCount = computed(() => {
        const stageCount = kanbanStages.value.reduce(
          (sum, stage) => sum + (stage.deals?.length || 0),
          0
        );
        return stageCount + (kanbanArchive.value.deals?.length || 0);
      });

      const tagChipStyle = (color) => {
        const c = color || '#64748b';
        return {
          backgroundColor: `${c}15`,
          color: c,
          borderColor: `${c}40`,
        };
      };

      // Format currency
      const formatCurrency = (value) => {
        return new Intl.NumberFormat('nl-NL', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value || 0);
      };

      // Handlers
      const closePipelineMenu = () => {
        pipelineMenuOpen.value = false;
      };

      const closeOwnerMenu = () => {
        ownerMenuOpen.value = false;
      };

      const closeStatsMenu = () => {
        statsMenuOpen.value = false;
      };

      const selectPipeline = (pipelineId) => {
        pipelineMenuOpen.value = false;
        if (pipelineId === currentPipelineId.value) return;
        dealStore.setCurrentPipeline(pipelineId);
      };

      const selectOwner = async (value) => {
        ownerMenuOpen.value = false;
        await applyOwnerFilter(value);
      };

      const goToStats = () => {
        statsMenuOpen.value = false;
        router.push({ name: 'Stats' });
      };

      const handleSearch = (value) => {
        searchQuery.value = value;

        // Debounce search
        if (searchTimeout.value) {
          clearTimeout(searchTimeout.value);
        }

        searchTimeout.value = setTimeout(async () => {
          dealStore.setSearchFilter(value);
          await dealStore.fetchKanban();
        }, 300);
      };

      const applyOwnerFilter = async (value) => {
        selectedOwner.value = value || '';
        dealStore.setOwnerFilter(selectedOwner.value || null);
        await dealStore.fetchKanban();
      };

      const applyTagFilter = async () => {
        dealStore.setTagFilter([...selectedTagIds.value]);
        await dealStore.fetchKanban();
      };

      const clearTagFilter = async () => {
        selectedTagIds.value = [];
        dealStore.setTagFilter([]);
        await dealStore.fetchKanban();
      };

      const clearAllFilters = async () => {
        selectedOwner.value = '';
        selectedTagIds.value = [];
        dealStore.setOwnerFilter(null);
        dealStore.setTagFilter([]);
        await dealStore.fetchKanban();
      };

      const loadMembers = async () => {
        const organizationId = userStore.currentOrganization?.id;
        if (!organizationId) return;
        try {
          members.value = await organizationService.getMembers(organizationId);
        } catch (error) {
          console.error('Failed to load members:', error);
        }
      };

      const handleDealClick = (deal) => {
        dealStore.openDealDrawer(deal.id);
        if (route.params.id !== deal.id) {
          router.push({ name: 'DealDetail', params: { id: deal.id } });
        }
      };

      const handleDealSelect = (dealId) => {
        dealStore.toggleDealSelection(dealId);
      };

      const handleDealEmail = (deal) => {
        const email = deal.lead?.email || deal.contact?.email;
        if (!email) return;
        composeEmail({ dealId: deal.id, email });
      };

      const handleAssignOwner = async (deal, ownerId) => {
        try {
          await dealStore.updateDeal(deal.id, { owner_id: ownerId });
          const member = members.value.find((item) => {
            const user = item.user || item;
            return (user?.id ?? item.user_id) === ownerId;
          });
          const user = member ? member.user || member : null;
          const owner = user
            ? {
                id: user.id ?? member.user_id,
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
              }
            : null;
          const stages = dealStore.kanbanData?.stages || [];
          for (const stage of stages) {
            const found = stage.deals?.find((item) => item.id === deal.id);
            if (!found) continue;
            found.owner = owner;
            found.owner_id = ownerId;
            break;
          }
          deal.owner = owner;
          deal.owner_id = ownerId;
          if (dealStore.currentDeal?.id === deal.id) {
            dealStore.currentDeal.owner_id = ownerId;
            dealStore.currentDeal.owner = owner;
          }
          toastStore.addToast({
            type: 'success',
            message: t('deals.deal_updated'),
          });
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: t('deals.update_failed'),
          });
        }
      };

      const handleStageChange = async (dealId, newStageId, oldStageId, fromArchive = false) => {
        const targetStage = kanbanStages.value.find((s) => s.id === newStageId);

        if (targetStage?.is_won) {
          await handleKanbanMarkWon(dealId, oldStageId);
          return;
        }

        try {
          await dealStore.moveDealToStage(dealId, newStageId, oldStageId, fromArchive);
          toastStore.addToast({
            type: 'success',
            message: t('deals.stage_updated'),
          });
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: t('deals.stage_update_failed'),
          });
        }
      };

      const handleKanbanMarkWon = async (dealId, oldStageId) => {
        try {
          const { proposals } = await dealService.getDealProposals(dealId);
          const acceptableStatuses = ['sent', 'viewed', 'rejected'];
          const nonAccepted = (proposals || []).filter((p) =>
            acceptableStatuses.includes(p.status)
          );

          const executeWon = async (acceptProposalIds = []) => {
            try {
              for (const { leadId, proposalId } of acceptProposalIds) {
                await crmService.markProposalAsAccepted(leadId, proposalId);
              }
              await dealStore.markDealAsWon(dealId);
              toastStore.addToast({
                type: 'success',
                message: t('deals.marked_as_won'),
              });
            } catch (error) {
              await dealStore.fetchKanban();
              toastStore.addToast({
                type: 'error',
                message: t('deals.mark_won_failed'),
              });
            }
          };

          if (nonAccepted.length > 0) {
            const modalId = uiStore.addModal(false, {
              title: t('deals.mark_won_confirm_title'),
              description: t('deals.mark_won_confirm_proposals', {
                count: nonAccepted.length,
              }),
              submitText: t('deals.mark_won_accept_proposals'),
              cancelText: t('deals.mark_won_skip_proposals'),
              onSubmit: async () => {
                uiStore.closeModal(modalId);
                const toAccept = nonAccepted.map((p) => ({
                  leadId: p.lead_id || p.lead?.id,
                  proposalId: p.id,
                }));
                await executeWon(toAccept);
              },
              onCancel: async () => {
                uiStore.closeModal(modalId);
                await executeWon();
              },
            });
          } else {
            await executeWon();
          }
        } catch (error) {
          await dealStore.fetchKanban();
          toastStore.addToast({
            type: 'error',
            message: t('deals.mark_won_failed'),
          });
        }
      };

      const handleDealUpdated = () => {
        dealStore.fetchKanban();
      };

      const handleDealDeleted = () => {
        dealStore.closeDealDrawer();
        if (route.params.id) {
          router.push({ name: 'Deals' });
        }
        dealStore.fetchKanban();
      };

      const closeDealDrawer = (options = {}) => {
        dealStore.closeDealDrawer();
        if (route.params.id && !options.skipRouteReset) {
          router.push({ name: 'Deals' });
        }
      };

      const clearSelection = () => {
        dealStore.clearSelection();
      };

      const closeBulkMoveMenu = () => {
        bulkMoveOpen.value = false;
      };

      const handleBulkMove = async (stageId) => {
        bulkMoveOpen.value = false;
        try {
          await dealStore.bulkMoveToStage(stageId);
          toastStore.addToast({
            type: 'success',
            message: t('deals.bulk_move_success'),
          });
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: t('deals.bulk_move_failed'),
          });
        }
      };

      const handleBulkArchive = async () => {
        try {
          await dealStore.bulkArchive();
          archiveExpanded.value = true;
          toastStore.addToast({
            type: 'success',
            message: t('deals.bulk_archive_success'),
          });
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: t('deals.bulk_archive_failed'),
          });
        }
      };

      const handleArchiveDrop = async (dealId) => {
        archiveExpanded.value = true;
        try {
          await dealStore.archiveDeal(dealId);
          toastStore.addToast({
            type: 'success',
            message: t('deals.bulk_archive_success'),
          });
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: t('deals.bulk_archive_failed'),
          });
        }
      };

      const handleExport = async () => {
        try {
          await dealStore.exportDeals();
          toastStore.addToast({
            type: 'success',
            message: t('deals.export_success'),
          });
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: t('deals.export_failed'),
          });
        }
      };

      const openMailInbox = () => {
        const modalId = uiStore.addModal(CustomerMailInboxModal, {
          title: t('deals.mail_inbox'),
          size: 'lg',
          onClose: () => {
            uiStore.closeModal(modalId);
            loadInboxCount();
          },
        });
      };

      const loadInboxCount = async () => {
        if (!dealEmailEnabled.value) {
          inboxCount.value = 0;
          return;
        }
        try {
          const result = await dealEmailService.getInboxCounts();
          inboxCount.value = result?.total || 0;
        } catch (error) {
          inboxCount.value = 0;
        }
      };

      const openCreateDealModal = (stage = null) => {
        const modalId = uiStore.addModal(CreateDealModal, {
          title: t('deals.create_deal'),
          pipelineId: currentPipelineId.value,
          stageId: stage?.id || null,
          onSubmit: async (deal) => {
            uiStore.closeModal(modalId);
            toastStore.addToast({
              type: 'success',
              message: t('deals.deal_created'),
            });
            await dealStore.fetchKanban();
          },
          onError: (errors) => {
            console.error('Deal creation errors:', errors);
          },
        });
      };

      const goToPipelineSettings = () => {
        router.push('/settings/pipelines');
      };

      // Keep the deal drawer in sync with the :id route param
      const syncDrawerWithRoute = (dealId) => {
        if (dealId) {
          if (dealStore.selectedDealId !== dealId || !dealStore.showDealDrawer) {
            dealStore.openDealDrawer(dealId);
          }
        } else if (dealStore.showDealDrawer) {
          dealStore.closeDealDrawer();
        }
      };

      // Initialize
      onMounted(async () => {
        // Mirror any persisted store filters into the local UI state
        selectedOwner.value = dealStore.filters.owner_id || '';
        selectedTagIds.value = [...(dealStore.filters.tag_ids || [])];
        searchQuery.value = dealStore.filters.search || '';

        loadMembers();
        tagStore.fetchTags().catch(() => {});
        loadInboxCount();
        await dealStore.fetchPipelines();
        if (dealStore.currentPipelineId) {
          await dealStore.fetchKanban();
        }

        // Open the drawer if the URL contains a deal id (e.g. /deals/:id)
        syncDrawerWithRoute(route.params.id);
      });

      // React to route param changes (back/forward navigation, deep links)
      watch(
        () => route.params.id,
        (newId) => {
          syncDrawerWithRoute(newId);
        }
      );

      // Watch for pipeline changes
      watch(currentPipelineId, async (newId) => {
        if (newId) {
          await dealStore.fetchKanban();
        }
      });

      watch(hasSelection, (selected) => {
        if (!selected) bulkMoveOpen.value = false;
      });

      return {
        searchQuery,
        bulkMoveOpen,
        archiveExpanded,
        viewMode,
        pipelines,
        pipelinesLoading,
        currentPipelineId,
        currentPipelineName,
        kanbanStages,
        kanbanArchive,
        kanbanTotals,
        kanbanLoading,
        exporting,
        showDealDrawer,
        selectedDealId,
        selectedDealIds,
        hasSelection,
        selectionCount,
        pipelineMenuOpen,
        ownerMenuOpen,
        statsMenuOpen,
        ownerAvatars,
        selectedOwnerLabel,
        closePipelineMenu,
        closeOwnerMenu,
        closeStatsMenu,
        selectPipeline,
        selectOwner,
        goToStats,

        selectedOwner,
        selectedTagIds,
        ownerOptions,
        tagOptions,
        hasActiveTagFilter,
        hasActiveFilters,
        filteredDealCount,
        tagChipStyle,
        applyTagFilter,
        clearTagFilter,
        clearAllFilters,

        formatCurrency,
        handleSearch,
        members,
        handleDealClick,
        handleDealSelect,
        handleDealEmail,
        handleAssignOwner,
        handleStageChange,
        handleDealUpdated,
        handleDealDeleted,
        closeDealDrawer,
        clearSelection,
        closeBulkMoveMenu,
        handleBulkMove,
        handleBulkArchive,
        handleArchiveDrop,
        handleExport,
        openMailInbox,
        inboxCount,
        dealEmailEnabled,
        openCreateDealModal,
        goToPipelineSettings,
      };
    },
  };
</script>

<style scoped lang="scss">
  .deals-page {
    &__header {
      display: flex;
      flex-direction: column;
      gap: 12px;
      margin-bottom: 12px;
    }

    &__title-row,
    &__toolbar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
    }

    &__title-group,
    &__actions,
    &__toolbar-left,
    &__filters {
      display: flex;
      align-items: center;
      min-width: 0;
    }

    &__title-group {
      gap: 8px;
    }

    &__actions,
    &__toolbar-left {
      gap: 8px;
      flex-shrink: 0;
    }

    &__title {
      margin: 0;
      font-size: 20px;
      font-weight: 400;
      line-height: 1;
      color: #000921;
      white-space: nowrap;
    }

    &__pipeline,
    &__stats,
    &__owner {
      position: relative;
    }

    &__pipeline-trigger,
    &__stats-trigger,
    &__owner-trigger,
    &__tags-trigger,
    &__mail-btn,
    &__icon-btn,
    &__add-btn {
      display: inline-flex;
      align-items: center;
      border: 1px solid #d7d7d7;
      background: #fff;
      color: #434f6f;
      font-size: 13px;
      font-weight: 400;
      line-height: 20px;
      cursor: pointer;

      &:focus-visible {
        outline: 2px solid #2f69ff;
        outline-offset: 2px;
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      img {
        display: block;
        flex-shrink: 0;
      }
    }

    &__pipeline-trigger {
      gap: 6px;
      padding: 0;
      border: 0;
      background: transparent;
      font-weight: 300;
      letter-spacing: 0.13px;
    }

    &__icon-btn {
      justify-content: center;
      padding: 10px;
      border-radius: 4px;
    }

    &__mail-btn {
      gap: 5px;
      padding: 8px;
      border-radius: 4px;
      font-weight: 500;
      line-height: 20px;
    }

    &__mail-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 4px;
      border-radius: 6px;
      background: #3364e6;
      color: #fff;
      font-size: 10px;
      font-weight: 500;
      line-height: 20px;
      text-transform: uppercase;
    }

    &__add-btn {
      gap: 10px;
      padding: 8px;
      border-color: #3364e6;
      border-radius: 4px;
      background: #3364e6;
      color: #fff;
      font-weight: 500;
      line-height: 20px;
    }

    &__view-toggle {
      display: inline-flex;
      align-items: center;
      padding: 4px 10px 4px 3px;
      border: 1px solid #d7d7d7;
      border-radius: 4px;
      background: #fff;
    }

    &__view-option {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      margin: 0;
      padding: 6px;
      border: 0;
      border-radius: 4px;
      background: transparent;
      color: #434f6f;
      font-size: 13px;
      font-weight: 400;
      line-height: 20px;
      cursor: pointer;

      &:focus-visible {
        outline: 2px solid #2f69ff;
        outline-offset: 2px;
      }

      &--active {
        background: #e3e6ee;
      }
    }

    &__stats-trigger {
      gap: 6px;
      padding: 10px;
      border-radius: 4px;
    }

    &__filters {
      border-radius: 4px;

      :deep(.deals-filter) {
        margin-left: -1px;
      }
    }

    &__search,
    &__owner-trigger,
    &__tags-trigger {
      height: 40px;
      background: #fff;
      border: 1px solid #d7d7d7;
    }

    &__search {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 194px;
      padding: 10px;
      border-radius: 4px 0 0 4px;

      input {
        width: 100%;
        min-width: 0;
        border: 0;
        padding: 0;
        background: transparent;
        color: #434f6f;
        font-size: 13px;
        font-weight: 300;
        letter-spacing: 0.13px;
        line-height: 20px;

        &::placeholder {
          color: #434f6f;
        }

        &:focus {
          outline: none;
        }
      }
    }

    &__owner-trigger,
    &__tags-trigger {
      gap: 6px;
      height: 40px;
      padding: 8px 10px;
      border-radius: 0;
      white-space: nowrap;
    }

    &__owner-trigger {
      margin-left: -1px;
    }

    &__tags-trigger {
      border-radius: 0 4px 4px 0;
    }

    &__owner-trigger--active,
    &__tags-trigger--active {
      background: #f4f7ff;
    }

    &__owner-avatars {
      display: inline-flex;
      align-items: center;
      height: 20px;
    }

    &__owner-avatar {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      margin-left: -6px;
      border: 1px solid #fff;
      border-radius: 50%;
      font-size: 8px;
      font-weight: 500;
      line-height: 1;

      &:first-child {
        margin-left: 0;
      }
    }

    &__tags-count {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 16px;
      padding: 2px 4px;
      border-radius: 6px;
      background: #2f69ff;
      color: #fff;
      font-size: 10px;
      line-height: 1;
    }

    &__menu {
      position: absolute;
      top: calc(100% + 6px);
      left: 0;
      z-index: 20;
      min-width: 220px;
      padding: 6px;
      background: #fff;
      border: 1px solid #d7d7d7;
      border-radius: 4px;
      box-shadow: 0 8px 24px rgb(0 9 33 / 8%);

      &--right {
        right: 0;
        left: auto;
      }

      &--stats {
        min-width: 240px;
      }
    }

    &__menu-item,
    &__menu-link {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      padding: 8px 10px;
      border: 0;
      border-radius: 4px;
      background: transparent;
      color: #000921;
      font-size: 13px;
      line-height: 20px;
      text-align: left;
      cursor: pointer;

      &:hover,
      &--active {
        background: #e3e6ee;
      }
    }

    &__menu-avatar {
      display: inline-flex;
      flex-shrink: 0;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      font-size: 8px;
      font-weight: 500;
      line-height: 1;

      &--all {
        background: #e3e6ee;
        color: #434f6f;
      }
    }

    &__menu-empty {
      margin: 0;
      padding: 8px 10px;
      color: #434f6f;
      font-size: 13px;
    }

    &__stat-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      padding: 8px 10px;
      color: #434f6f;
      font-size: 13px;
      font-weight: 300;

      strong {
        color: #000921;
        font-weight: 400;
      }
    }

    &__menu-link {
      margin-top: 4px;
      border-top: 1px solid #e3e6ee;
      border-radius: 0;
      color: #2f69ff;
    }

    @include lg-down {
      &__title-row,
      &__toolbar {
        align-items: stretch;
        flex-direction: column;
      }

      &__actions,
      &__toolbar-left,
      &__filters {
        flex-wrap: wrap;
      }

      &__search {
        width: 100%;
        border-radius: 4px;
      }

      &__owner-trigger,
      &__tags-trigger {
        border-radius: 4px;
      }
    }

    &__bulk-dock {
      position: fixed;
      z-index: 40;
      bottom: 24px;
      left: calc(50% + 130px);
      pointer-events: none;
      transform: translateX(-50%);

      @include lg-down {
        bottom: calc(72px + env(safe-area-inset-bottom));
        left: 50%;
      }

      &.deals-bulk-enter-active .deals-page__bulk-actions,
      &.deals-bulk-leave-active .deals-page__bulk-actions {
        transition:
          transform 0.34s cubic-bezier(0.22, 1, 0.36, 1),
          opacity 0.22s ease;
      }

      &.deals-bulk-enter-from .deals-page__bulk-actions,
      &.deals-bulk-leave-to .deals-page__bulk-actions {
        opacity: 0;
        transform: translateY(16px) scale(0.97);
      }

      @media (prefers-reduced-motion: reduce) {
        &.deals-bulk-enter-active .deals-page__bulk-actions,
        &.deals-bulk-leave-active .deals-page__bulk-actions {
          transition: opacity 0.15s ease;
        }

        &.deals-bulk-enter-from .deals-page__bulk-actions,
        &.deals-bulk-leave-to .deals-page__bulk-actions {
          transform: none;
        }
      }
    }

    &__bulk-actions {
      display: flex;
      align-items: center;
      gap: 2px;
      padding: 6px;
      border-radius: 12px;
      background: var(--slate-900);
      color: #fff;
      box-shadow:
        0 16px 40px rgb(24 24 27 / 28%),
        0 2px 8px rgb(24 24 27 / 18%);
      pointer-events: auto;
    }

    &__bulk-count {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      height: 32px;
      padding: 0 4px 0 12px;
      border-radius: 8px;
      background: rgb(255 255 255 / 8%);
      font-size: 13px;
      font-weight: 500;
      line-height: 1;
      white-space: nowrap;
    }

    &__bulk-clear,
    &__bulk-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: 0;
      background: transparent;
      color: inherit;
      cursor: pointer;

      &:focus-visible {
        outline: 2px solid #fff;
        outline-offset: 2px;
      }

      @include hover {
        background: rgb(255 255 255 / 12%);
      }
    }

    &__bulk-clear {
      width: 22px;
      height: 22px;
      padding: 0;
      border-radius: 6px;
    }

    &__bulk-btn {
      gap: 6px;
      height: 32px;
      padding: 0 10px;
      border-radius: 8px;
      font-size: 13px;
      font-weight: 500;
      line-height: 1;
      white-space: nowrap;
    }

    &__bulk-move {
      position: relative;
    }

    &__bulk-menu {
      position: absolute;
      right: 0;
      bottom: calc(100% + 8px);
      z-index: 2;
      width: max-content;
      min-width: 200px;
      max-width: 280px;
      max-height: 280px;
      overflow-y: auto;
      padding: 6px;
      background: #fff;
      border: 1px solid #d7d7d7;
      border-radius: 8px;
      box-shadow: 0 12px 32px rgb(0 9 33 / 16%);
    }

    &__bulk-menu-label {
      margin: 0;
      padding: 6px 10px 4px;
      color: #434f6f;
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.04em;
      line-height: 16px;
      text-transform: uppercase;
    }

    &__bulk-menu-item {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      padding: 8px 10px;
      border: 0;
      border-radius: 4px;
      background: transparent;
      color: #000921;
      font-size: 13px;
      line-height: 20px;
      text-align: left;
      cursor: pointer;

      span:last-child {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      &:hover,
      &:focus-visible {
        background: #e3e6ee;
        outline: none;
      }
    }

    &__bulk-menu-dot {
      flex-shrink: 0;
      width: 8px;
      height: 8px;
      border-radius: 50%;
    }

    &__bulk-divider {
      width: 1px;
      height: 20px;
      margin: 0 4px;
      background: rgb(255 255 255 / 16%);
    }

    :global(body:has(.sidebar--collapsed)) &__bulk-dock {
      left: calc(50% + 36px);

      @include lg-down {
        left: 50%;
      }
    }

    &__skeleton {
      display: flex;
      gap: 0.75rem;
      align-items: flex-start;
      overflow: hidden;

      :deep(.skeleton__text) {
        margin-bottom: 0;
      }

      @include lg-down {
        flex-direction: column;
      }
    }

    &__skeleton-column {
      flex: 1 1 0;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      padding: 0.75rem;
      background: var(--slate-50);
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;

      @include lg-down {
        width: 100%;
        flex: none;
      }
    }

    &__skeleton-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      padding: 0.25rem 0.25rem 0.5rem;
    }

    &__skeleton-card {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      padding: 0.875rem;
      background: var(--white);
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
    }
  }

  .filter-active {
    border-color: var(--brand-300);
    color: var(--brand-600);
  }

  .filter-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 16px;
    height: 16px;
    padding: 0 3px;
    background: var(--brand-600);
    color: #fff;
    border-radius: 8px;
    font-size: 0.625rem;
    font-weight: 700;
    margin-left: 4px;
  }

  .deals-filter {
    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 4px 8px 8px;
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--slate-500);
      text-transform: uppercase;
      letter-spacing: 0.025em;
    }

    &__reset {
      border: none;
      background: none;
      padding: 0;
      font-size: 0.75rem;
      font-weight: 500;
      color: var(--brand-600);
      cursor: pointer;
      text-transform: none;
      letter-spacing: normal;

      @include hover {
        text-decoration: underline;
      }
    }

    &__options {
      display: flex;
      flex-direction: column;
      gap: 2px;
      max-height: 280px;
      overflow-y: auto;
    }

    &__option {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 8px;
      border-radius: 0.375rem;
      cursor: pointer;

      @include hover {
        background: var(--slate-50);
      }

      input[type='checkbox'] {
        accent-color: var(--brand-600);
        cursor: pointer;
      }
    }

    &__tag {
      display: inline-flex;
      align-items: center;
      padding: 0.125rem 0.5rem;
      border-radius: 9999px;
      border: 1px solid;
      font-size: 0.75rem;
      font-weight: 500;
      line-height: 1.4;
    }

    &__empty {
      padding: 0.5rem 0.5rem 0.25rem;
      font-size: 0.8125rem;
      color: var(--slate-400);
      text-align: center;
    }
  }

  .inbox-btn {
    position: relative;

    &--active {
      color: var(--brand-600);
    }

    &__badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 18px;
      height: 18px;
      padding: 0 4px;
      background: var(--red-500);
      color: #fff;
      border-radius: 9px;
      font-size: 0.6875rem;
      font-weight: 700;
      margin-left: 4px;
      line-height: 1;
    }
  }
</style>
